import React, { useState, useEffect, useRef, useMemo } from 'react';
import { doc, getDoc, collection, addDoc, serverTimestamp, updateDoc, runTransaction, onSnapshot, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../config/firebase';
import { sendEmail } from '../../utils/helpers';

export default function RsvpPage({ inviteId, previewData, onClosePreview }) {
    const [eventDetails, setEventDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [isAttending, setIsAttending] = useState(true);
    const [plusOne, setPlusOne] = useState(false);
    const [plusOneName, setPlusOneName] = useState('');
    const [mealChoice, setMealChoice] = useState('');
    const [plusOneMealChoice, setPlusOneMealChoice] = useState('');
    const [error, setError] = useState('');
    const [attendees, setAttendees] = useState([]);
    const [guestListVisible, setGuestListVisible] = useState(true);
    const [hostName, setHostName] = useState('');
    
    const [timeLeft, setTimeLeft] = useState(null);
    const isAppleDevice = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

    const [customAnswers, setCustomAnswers] = useState({});
    const [pollAnswers, setPollAnswers] = useState({});
    const [selectedPotluckItems, setSelectedPotluckItems] = useState([]);

    const [songRequest, setSongRequest] = useState(''); 
    const [songSearchQuery, setSongSearchQuery] = useState('');
    const [songSearchResults, setSongSearchResults] = useState([]);
    const [isSearchingSongs, setIsSearchingSongs] = useState(false);
    const [selectedSong, setSelectedSong] = useState(null);
    const [playlist, setPlaylist] = useState([]);
    
    const [playingPreview, setPlayingPreview] = useState(null);
    const audioRef = useRef(null);

    const [guestbookMessages, setGuestbookMessages] = useState([]);
    const [newGuestbookMessage, setNewGuestbookMessage] = useState('');
    const [guestbookLoading, setGuestbookLoading] = useState(true);

    const [submitted, setSubmitted] = useState(false);
    const [isOpened, setIsOpened] = useState(!!previewData); 
    const [photoTab, setPhotoTab] = useState('gallery'); 

    const [photos, setPhotos] = useState([]);
    const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);

    useEffect(() => {
        if (previewData) {
            setEventDetails(previewData);
            setGuestListVisible(previewData.showGuestList !== false);
            setHostName(previewData.hostName || "The Host");
            setGuestbookMessages([
                { id: '1', name: 'Alice', message: 'So excited for this!', createdAt: new Date() },
                { id: '2', name: 'Bob', message: 'Can I bring my famous dip?', createdAt: new Date() }
            ]);
            setGuestbookLoading(false);
            setLoading(false);
            return;
        }

        if (!inviteId) return;

        if (!db) {
            setError("Database is required to load this invitation. Please refresh the page.");
            setLoading(false);
            return;
        }

        const [userId, eventId, guestId] = inviteId.split('_');
        
        const eventRef = doc(db, `users/${userId}/evites/${eventId}`);
        const unsubEvent = onSnapshot(eventRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setEventDetails(data);
                setGuestListVisible(data.showGuestList !== false);
                setLoading(false);
            } else {
                setError("This invitation is not valid or has been removed.");
                setLoading(false);
            }
        }, (err) => {
            console.error("Error fetching event:", err);
            setError("Could not load invitation details.");
            setLoading(false);
        });

        const guestRef = doc(db, `users/${userId}/evites/${eventId}/guests/${guestId}`);
        getDoc(guestRef).then(guestSnap => {
            if (guestSnap.exists()) {
                const guestData = guestSnap.data();
                setName(guestData.name || '');
                setEmail(guestData.email || '');
                
                if (guestData.status === 'Attending' || guestData.status === 'Declined') {
                    setSubmitted(true);
                    setIsAttending(guestData.status === 'Attending');
                }
                
                if (guestData.hasPlusOne) {
                    setPlusOne(true);
                    setPlusOneName(guestData.plusOneName || '');
                }
                
                if (guestData.mealChoice) setMealChoice(guestData.mealChoice);
                if (guestData.plusOneMealChoice) setPlusOneMealChoice(guestData.plusOneMealChoice);
                if (guestData.customAnswers) setCustomAnswers(guestData.customAnswers);
                if (guestData.pollAnswers) setPollAnswers(guestData.pollAnswers);
                if (guestData.potluckItems) setSelectedPotluckItems(guestData.potluckItems);
                
                if (guestData.songRequestData) {
                    setSelectedSong(guestData.songRequestData);
                } else if (guestData.songRequest) {
                    setSongSearchQuery(guestData.songRequest);
                }
            }
        }).catch(err => console.error("Error loading guest:", err));

        return () => unsubEvent();
    }, [inviteId, db, previewData]);

    useEffect(() => {
        if (previewData || !eventDetails || !eventDetails.enablePhotoDrop || !db) return;
        
        const [userId, eventId] = inviteId.split('_');
        const unsubPhotos = onSnapshot(collection(db, `events/${eventId}/guest_photos`), (snapshot) => {
            const fetched = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            fetched.sort((a, b) => (b.uploadedAt?.toMillis() || 0) - (a.uploadedAt?.toMillis() || 0));
            setPhotos(fetched);
        });
        return () => unsubPhotos();
    }, [eventDetails?.enablePhotoDrop, inviteId, db, previewData]);

    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        if (previewData) {
            alert("Photo uploads are disabled in preview mode.");
            return;
        }
        
        setIsUploadingPhotos(true);
        try {
            const [userId, eventId] = inviteId.split('_');

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const storageRef = ref(storage, `events/${eventId}/guest_photos/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                
                await addDoc(collection(db, `events/${eventId}/guest_photos`), {
                    url,
                    uploadedAt: serverTimestamp(),
                    guestName: name || 'Guest'
                });
            }
            setPhotoTab('gallery');
        } catch (err) {
            console.error("Upload failed", err);
            alert("Upload failed. Please try again.");
        } finally {
            setIsUploadingPhotos(false);
        }
    };

    useEffect(() => {
        if (previewData) return;
        if (!eventDetails?.pageSettings?.allowSongRequests) return;
        if (!db) return;

        const [userId, eventId] = inviteId.split('_');
        const guestsRef = collection(db, `users/${userId}/evites/${eventId}/guests`);
        
        const unsub = onSnapshot(guestsRef, (snap) => {
            const list = snap.docs
                .map(d => d.data())
                .filter(g => g.songRequest || g.songRequestData)
                .map(g => ({
                    songRequest: g.songRequest,
                    songRequestData: g.songRequestData,
                    name: g.name
                }));
            setPlaylist(list);
        });
        return () => unsub();
    }, [inviteId, db, eventDetails?.pageSettings?.allowSongRequests, previewData]);

    useEffect(() => {
        if (previewData || !eventDetails || eventDetails.enableGuestbook === false) {
            setGuestbookLoading(false);
            return;
        }
        
        if (!db) {
            setGuestbookLoading(false);
            return;
        }
        
        const [userId, eventId] = inviteId.split('_');
        const guestbookCol = collection(db, `users/${userId}/evites/${eventId}/guestbook`);
        const qGuestbook = query(guestbookCol, orderBy('createdAt', 'desc'));
        
        const unsubGuestbook = onSnapshot(qGuestbook, (snapshot) => {
            setGuestbookMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setGuestbookLoading(false);
        });

        return () => unsubGuestbook();
    }, [eventDetails?.enableGuestbook, inviteId, db, previewData]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const togglePreview = (url) => {
        if (audioRef.current) {
            audioRef.current.pause();
            if (playingPreview === url) {
                setPlayingPreview(null);
                audioRef.current = null;
                return;
            }
        }
        if (url) {
            const audio = new Audio(url);
            audio.volume = 0.5;
            audio.play().catch(e => console.error("Audio play failed", e));
            audio.onended = () => setPlayingPreview(null);
            audioRef.current = audio;
            setPlayingPreview(url);
        }
    };

    useEffect(() => {
        if (!eventDetails?.date || !eventDetails?.time) return;
        
        const targetDate = new Date(`${eventDetails.date}T${eventDetails.time}`);
        
        const calculateTimeLeft = () => {
            const difference = +targetDate - +new Date();
            if (difference > 0) {
                return {
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60)
                };
            }
            return null; 
        };

        setTimeLeft(calculateTimeLeft());

        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, [eventDetails]);

    const handlePotluckSelection = (itemId) => {
        setSelectedPotluckItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    const searchTracks = async () => {
        if (!songSearchQuery.trim()) return;
        setIsSearchingSongs(true);
        try {
            const res = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(songSearchQuery)}&entity=song&limit=5`);
            const data = await res.json();
            setSongSearchResults(data.results);
        } catch (e) {
            console.error("Song search failed", e);
        } finally {
            setIsSearchingSongs(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (songSearchQuery.trim().length >= 2) {
                searchTracks();
            } else {
                setSongSearchResults([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [songSearchQuery]);

    const handlePostGuestbook = async (e) => {
        e.preventDefault();
        if (!newGuestbookMessage.trim()) return;
        
        if (previewData) {
            setGuestbookMessages(prev => [{ id: Date.now().toString(), name: name || 'Guest', message: newGuestbookMessage, createdAt: new Date() }, ...prev]);
            setNewGuestbookMessage('');
            return;
        }

        if (!name) {
            alert("Please enter your name in the RSVP form above first.");
            return;
        }

        try {
            const [userId, eventId] = inviteId.split('_');
            await addDoc(collection(db, `users/${userId}/evites/${eventId}/guestbook`), {
                name: name,
                message: newGuestbookMessage,
                createdAt: serverTimestamp()
            });
            setNewGuestbookMessage('');
        } catch (err) {
            console.error("Error posting to guestbook:", err);
            alert("Failed to post message.");
        }
    };

    useEffect(() => {
        if (submitted) {
            window.scrollTo(0, 0);
            setTimeout(() => {
                const el = document.getElementById('rsvp-success-view');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [submitted]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (previewData) {
            if (isAttending && window.confetti && eventDetails.pageSettings?.effect) {
                 const effect = eventDetails.pageSettings.effect;
                 if (effect === 'fireworks') {
                    const duration = 3 * 1000; 
                    const animationEnd = Date.now() + duration;
                    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
                    const random = (min, max) => Math.random() * (max - min) + min;
                    const interval = setInterval(function() {
                        const timeLeft = animationEnd - Date.now();
                        if (timeLeft <= 0) return clearInterval(interval);
                        const particleCount = 50 * (timeLeft / duration);
                        window.confetti(Object.assign({}, defaults, { particleCount, origin: { x: random(0.1, 0.3), y: Math.random() - 0.2 } }));
                        window.confetti(Object.assign({}, defaults, { particleCount, origin: { x: random(0.7, 0.9), y: Math.random() - 0.2 } }));
                    }, 250);
                 } else if (effect === 'confetti') {
                    window.confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, colors: [eventDetails.themeColor || '#ec4899', '#ffffff', '#ffd700'] });
                 }
            }
            alert("This is a preview. RSVPs are disabled.");
            return;
        }

        if (!name) {
            setError("Please enter your name.");
            return;
        }
        
        try {
            const [userId, eventId, guestId] = inviteId.split('_');
            const guestRef = doc(db, `users/${userId}/evites/${eventId}/guests/${guestId}`);

            if (eventDetails.isPotluck) {
                const eventRef = doc(db, `users/${userId}/evites/${eventId}`);
                await runTransaction(db, async (transaction) => {
                    const eventDoc = await transaction.get(eventRef);
                    if (!eventDoc.exists()) throw "Event not found";
                    
                    const data = eventDoc.data();
                    let currentItems = data.potluckItems || [];
                    
                    let itemsUpdated = false;
                    const updatedItems = currentItems.map(item => {
                        if (isAttending && selectedPotluckItems.includes(item.id)) {
                            if (item.assignee && item.assignee !== name) {
                                throw new Error(`Sorry, "${item.text}" was just taken by ${item.assignee}. Please select something else.`);
                            }
                            if (item.assignee !== name) {
                                itemsUpdated = true;
                                return { ...item, assignee: name };
                            }
                        } 
                        else if (item.assignee === name) {
                             if (!isAttending || !selectedPotluckItems.includes(item.id)) {
                                 itemsUpdated = true;
                                 return { ...item, assignee: null };
                             }
                        }
                        return item;
                    });

                    if (itemsUpdated) {
                        transaction.update(eventRef, { potluckItems: updatedItems });
                    }
                });
            }

            const rsvpData = {
                status: isAttending ? 'Attending' : 'Declined',
                name: name,
                hasPlusOne: isAttending && eventDetails.allowPlusOnes && plusOneName.trim() !== '',
                plusOneName: isAttending && eventDetails.allowPlusOnes ? plusOneName.trim() : '',
                mealChoice: isAttending && eventDetails.mealOptions?.length > 0 ? mealChoice : '',
                plusOneMealChoice: isAttending && eventDetails.allowPlusOnes && plusOneName.trim() !== '' && eventDetails.mealOptions?.length > 0 ? plusOneMealChoice : '',
                customAnswers: customAnswers,
                pollAnswers: isAttending ? pollAnswers : {},
                potluckItems: isAttending ? selectedPotluckItems : [],
                songRequest: isAttending ? (selectedSong ? `${selectedSong.artistName} - ${selectedSong.trackName}` : songSearchQuery) : '',
                songRequestData: isAttending ? selectedSong : null
            };

            await updateDoc(guestRef, rsvpData);
            setSubmitted(true);
            
            if (isAttending && window.confetti) {
                const effect = eventDetails.pageSettings?.effect || 'confetti';
                
                if (effect === 'fireworks') {
                    const duration = 5 * 1000;
                    const animationEnd = Date.now() + duration;
                    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };
                    const random = (min, max) => Math.random() * (max - min) + min;
                    const interval = setInterval(function() {
                        const timeLeft = animationEnd - Date.now();
                        if (timeLeft <= 0) return clearInterval(interval);
                        const particleCount = 50 * (timeLeft / duration);
                        window.confetti(Object.assign({}, defaults, { particleCount, origin: { x: random(0.1, 0.3), y: Math.random() - 0.2 } }));
                        window.confetti(Object.assign({}, defaults, { particleCount, origin: { x: random(0.7, 0.9), y: Math.random() - 0.2 } }));
                    }, 250);
                } else if (effect === 'confetti') {
                    window.confetti({
                        particleCount: 150,
                        spread: 70,
                        origin: { y: 0.6 },
                        colors: [eventDetails.themeColor || '#ec4899', '#ffffff', '#ffd700']
                    });
                }
            }

        } catch (err) {
            console.error(err);
            setError("There was an error submitting your RSVP. Please try again. " + (err.message || err));
        }
    };

    const addToCalendarLinks = () => {
        if (!eventDetails) return { google: '', ics: '', icsContent: '' };
        
        const startDateTime = new Date(`${eventDetails.date}T${eventDetails.time}`);
        const endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000); 
        
        const formatDate = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, "");
        
        const details = `You're invited to ${eventDetails.title}! Location: ${eventDetails.location}`;
        const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventDetails.title)}&dates=${formatDate(startDateTime)}/${formatDate(endDateTime)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(eventDetails.location)}`;
        
        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'BEGIN:VEVENT',
            `DTSTART:${formatDate(startDateTime)}`,
            `DTEND:${formatDate(endDateTime)}`,
            `SUMMARY:${eventDetails.title}`,
            `DESCRIPTION:${details}`,
            `LOCATION:${eventDetails.location}`,
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\n');

        const icsUrl = `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`;

        return { google: googleUrl, ics: icsUrl, icsContent };
    };

    const handleIcsClick = (e) => {
        e.preventDefault();
        const { ics, icsContent } = addToCalendarLinks();
        if (!ics) return;

        const isSafari = typeof navigator !== 'undefined' && /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

        if (isAppleDevice && isSafari) {
             window.location.href = ics;
        } else {
             const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
             const url = window.URL.createObjectURL(blob);
             const link = document.createElement('a');
             link.href = url;
             link.setAttribute('download', 'event.ics');
             document.body.appendChild(link);
             link.click();
             document.body.removeChild(link);
             setTimeout(() => window.URL.revokeObjectURL(url), 100);
        }
    };

    if (loading) return <div className="text-center p-8">Loading Invitation...</div>;
    if (error) return <div className="text-center p-8 bg-red-100 text-red-700 rounded-lg m-4">{error}</div>;

    const getEviteTheme = (themeId) => {
        switch(themeId) {
            case 'classic': return { bg: 'bg-stone-100', card: 'bg-stone-50', text: 'text-stone-900', muted: 'text-stone-600', border: 'border-stone-300', font: 'font-serif', inputBg: 'bg-white' };
            case 'dark-elegance': return { bg: 'bg-gray-900', card: 'bg-gray-800', text: 'text-gray-100', muted: 'text-gray-400', border: 'border-gray-600', font: 'font-serif', inputBg: 'bg-gray-700' };
            case 'rustic': return { bg: 'bg-[#fefae0]', card: 'bg-[#faedcd]', text: 'text-[#3a4035]', muted: 'text-[#5f6c5a]', border: 'border-[#d4a373]', font: 'font-serif', inputBg: 'bg-[#fefae0]' };
            case 'modern': return { bg: 'bg-gray-200', card: 'bg-white', text: 'text-black', muted: 'text-gray-500', border: 'border-black', font: 'font-sans tracking-tight', inputBg: 'bg-gray-50' };
            case 'ocean': return { bg: 'bg-sky-50', card: 'bg-white', text: 'text-slate-800', muted: 'text-slate-500', border: 'border-sky-200', font: 'font-sans', inputBg: 'bg-sky-50' };
            case 'vintage': return { bg: 'bg-orange-50', card: 'bg-[#fffbf0]', text: 'text-amber-900', muted: 'text-amber-700', border: 'border-amber-200', font: 'font-serif', inputBg: 'bg-orange-100' };
            case 'neon': return { bg: 'bg-black', card: 'bg-gray-900', text: 'text-white', muted: 'text-fuchsia-400', border: 'border-fuchsia-500', font: 'font-sans tracking-wide', inputBg: 'bg-gray-800' };
            case 'garden': return { bg: 'bg-green-50', card: 'bg-white', text: 'text-green-900', muted: 'text-green-700', border: 'border-green-200', font: 'font-serif', inputBg: 'bg-green-50' };
            case 'playful':
            default: return { bg: 'bg-gray-50', card: 'bg-white', text: 'text-gray-900', muted: 'text-gray-600', border: 'border-gray-200', font: 'font-sans', inputBg: 'bg-gray-50' };
        }
    };
    
    const t = getEviteTheme(eventDetails?.eviteTheme);

    if (!isOpened) {
        return (
            <div className={`min-h-screen ${t.bg} flex flex-col items-center justify-center p-4 relative overflow-hidden`} onClick={() => setIsOpened(true)}>
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] blur-[120px] rounded-full pointer-events-none opacity-40" style={{ backgroundColor: eventDetails?.themeColor || '#ec4899' }}></div>
                
                <div className="relative z-10 text-center transform transition-all hover:scale-105 cursor-pointer flex flex-col items-center animate-fade-in-up">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl animate-bounce" style={{ animationDuration: '2.5s' }}>
                        <span className="text-4xl sm:text-5xl">💌</span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight drop-shadow-lg px-4">{eventDetails?.title || 'You\'re Invited'}</h1>
                    {eventDetails?.date && (
                        <p className="text-gray-300 font-bold tracking-widest uppercase text-xs sm:text-sm mb-12 drop-shadow">
                            {new Date(eventDetails.date.replace(/-/g, '/')).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </p>
                    )}
                    <button 
                        className="bg-white px-10 py-4 rounded-full font-bold shadow-xl hover:bg-gray-100 transition-all uppercase tracking-widest text-sm"
                        style={{ color: eventDetails?.themeColor || '#ec4899' }}
                    >
                        Tap to Open
                    </button>
                </div>
            </div>
        );
    }

    const themeColor = eventDetails.themeColor || '#ec4899'; 
    const bgStyle = { backgroundColor: themeColor };
    const textStyle = { color: themeColor };
    const borderStyle = { borderColor: themeColor };

    const renderSpotify = () => {
        if (!eventDetails.pageSettings?.spotifyLink) return null;
        return (
            <a href={eventDetails.pageSettings.spotifyLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-[#1DB954] text-white rounded-full text-sm font-bold shadow hover:bg-[#1ed760] transition mb-4">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.48.66.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                Listen to Playlist
            </a>
        );
    };

    const renderRegistry = () => {
        if (!eventDetails.pageSettings?.registryLinks?.length) return null;
        return (
            <div className="mb-8">
                <h3 className={`text-lg font-bold mb-3 text-center flex items-center justify-center gap-2 ${t.text}`}>
                    🎁 Gift Registry
                </h3>
                <div className="grid grid-cols-1 gap-2">
                    {eventDetails.pageSettings.registryLinks.map((link, idx) => (
                        <a 
                            key={idx} 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`flex items-center justify-between px-4 py-3 ${t.card} border ${t.border} rounded-xl shadow-sm hover:shadow-md hover:border-pink-300 transition group`}
                        >
                            <span className={`font-semibold group-hover:text-pink-600 ${t.text}`}>{link.title}</span>
                            <div className={`flex items-center group-hover:text-pink-500 ${t.muted}`}>
                                <span className="text-xs mr-2 font-medium">View</span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        );
    };

    const renderTimeline = () => {
        if (!eventDetails.timeline || eventDetails.timeline.length === 0) return null;
        return (
            <div className="mb-8">
                <h3 className={`text-lg font-bold mb-4 text-center ${t.text}`}>Event Schedule</h3>
                <div className={`relative border-l-2 ${t.border} ml-4 space-y-6`}>
                    {eventDetails.timeline.sort((a,b) => a.time.localeCompare(b.time)).map((item, idx) => (
                        <div key={idx} className="relative pl-6">
                            <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-2 border-pink-500"></div>
                            <span className="block text-xs font-bold text-pink-500 uppercase tracking-wide mb-1">
                                {new Date(`1970-01-01T${item.time}`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                            </span>
                            <h4 className={`font-semibold ${t.text}`}>{item.title}</h4>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderGuestbook = () => {
        if (eventDetails.enableGuestbook === false) return null;
        return (
            <div className={`mb-8 border-t ${t.border} pt-8`}>
                <h3 className={`text-lg font-bold mb-4 text-center ${t.text}`}>Guestbook</h3>
                
                <div className={`${t.inputBg} rounded-xl p-4 mb-4 max-h-60 overflow-y-auto space-y-3 border ${t.border}`}>
                    {guestbookMessages.length > 0 ? guestbookMessages.map((msg) => (
                        <div key={msg.id} className={`${t.card} p-3 rounded-lg shadow-sm border ${t.border}`}>
                            <div className="flex justify-between items-baseline mb-1">
                                <span className={`font-bold text-sm ${t.text}`}>{msg.name}</span>
                                <span className={`text-xs ${t.muted}`}>
                                    {msg.createdAt?.seconds ? new Date(msg.createdAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                                </span>
                            </div>
                            <p className={`text-sm ${t.muted}`}>{msg.message}</p>
                        </div>
                    )) : (
                        <p className={`text-center ${t.muted} text-sm py-4`}>Be the first to leave a message!</p>
                    )}
                </div>

                <div className="flex gap-2">
                    <input 
                        type="text" 
                        value={newGuestbookMessage}
                        onChange={(e) => setNewGuestbookMessage(e.target.value)}
                        placeholder={name ? `Say something, ${name}...` : "Enter your name above to post..."}
                        className={`flex-grow p-3 ${t.card} border ${t.border} rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 ${t.text}`}
                        style={{ '--tw-ring-color': themeColor }}
                        disabled={!name}
                    />
                    <button 
                        onClick={handlePostGuestbook}
                        disabled={!name || !newGuestbookMessage.trim()}
                        className="text-white px-4 rounded-xl font-bold text-sm disabled:opacity-50 hover:opacity-80 transition"
                        style={bgStyle}
                    >
                        Post
                    </button>
                </div>
            </div>
        );
    };

    const renderPotluck = () => {
        if (!eventDetails.isPotluck || !eventDetails.potluckItems?.length) return null;
        
        return (
            <div className="mb-8">
                <h3 className={`font-bold text-lg mb-3 flex items-center ${t.text}`}>
                    🍲 Potluck Sign-up
                </h3>
                <div className={`${t.card} rounded-xl border ${t.border} overflow-hidden`}>
                    <div className="bg-orange-50 px-4 py-2 border-b border-orange-100">
                        <p className="text-sm text-orange-800">Please pick an item to bring!</p>
                    </div>
                    <div className={`divide-y ${t.border.replace('border-', 'divide-')}`}>
                        {eventDetails.potluckItems.map((item) => (
                            <div key={item.id} className="p-3 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <span className="text-xl">
                                        {item.assignee ? '✅' : '🍽️'}
                                    </span>
                                    <div>
                                        <p className={`font-medium ${item.assignee ? `${t.muted} line-through` : t.text}`}>
                                            {item.text}
                                        </p>
                                        {item.assignee && (
                                            <p className="text-xs text-green-600 font-semibold">
                                                Brought by {item.assignee}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {isAttending && (!item.assignee || item.assignee === name) && !submitted && (
                                    <input 
                                        type="checkbox" 
                                        checked={selectedPotluckItems.includes(item.id)}
                                        onChange={() => handlePotluckSelection(item.id)}
                                        className="h-5 w-5 text-orange-500 focus:ring-orange-500 border-gray-300 rounded cursor-pointer"
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    if (submitted) {
        const successTitle = eventDetails.pageSettings?.goingMessage || "You're on the list!";
        const declineTitle = eventDetails.pageSettings?.notGoingMessage || "Sorry you can't make it!";
        
        return (
            <div id="rsvp-success-view" className={`min-h-screen ${t.bg} flex items-center justify-center p-4`}>
                <div className={`max-w-md w-full ${t.card} border ${t.border} shadow-xl rounded-3xl overflow-hidden text-center p-8`}>
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isAttending ? 'bg-green-100 text-green-500' : 'bg-gray-100 text-gray-500'}`}>
                        {isAttending ? (
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                        ) : (
                            <span className="text-3xl">👋</span>
                        )}
                    </div>
                    <h2 className={`text-3xl font-bold mb-2 ${t.text}`}>{isAttending ? successTitle : declineTitle}</h2>
                    <p className={`${t.muted} mb-6`}>
                        {isAttending ? `Thanks for RSVPing to ${eventDetails.title}.` : `We'll miss you at ${eventDetails.title}.`}
                    </p>
                    
                    {isAttending && (
                        <div className="flex flex-col gap-3">
                            {isAppleDevice ? (
                                <button onClick={handleIcsClick} className="w-full py-3 rounded-xl font-bold text-white shadow-md transition transform hover:scale-105" style={bgStyle}>
                                    Add to Apple Calendar
                                </button>
                            ) : (
                                <a href={addToCalendarLinks().google} target="_blank" rel="noopener noreferrer" className="w-full py-3 rounded-xl font-bold text-white shadow-md transition transform hover:scale-105" style={bgStyle}>
                                    Add to Google Calendar
                                </a>
                            )}
                            
                            {isAppleDevice ? (
                                <a 
                                    href={addToCalendarLinks().google} 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`w-full py-3 rounded-xl font-bold ${t.inputBg} ${t.text} border ${t.border} hover:opacity-80 block`}
                                >
                                    Add to Google Calendar
                                </a>
                            ) : (
                                <button 
                                    onClick={handleIcsClick}
                                    className={`w-full py-3 rounded-xl font-bold ${t.inputBg} ${t.text} border ${t.border} hover:opacity-80 block`}
                                >
                                    Add to Apple/Outlook
                                </button>
                            )}
                        </div>
                    )}

                    <button onClick={() => setSubmitted(false)} className={`w-full py-3 rounded-xl font-bold ${t.card} border ${t.border} ${t.text} hover:opacity-80 mt-4`}>
                        Update RSVP
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${t.bg} ${t.font} ${t.text}`}>
            {previewData && (
                <div className="fixed top-4 right-4 z-[210]">
                    <button 
                        onClick={onClosePreview} 
                        className="bg-white text-gray-800 px-6 py-2 rounded-full shadow-xl font-bold hover:bg-gray-100 border border-gray-200 flex items-center gap-2"
                    >
                        <span>✕</span> Close Preview
                    </button>
                </div>
            )}
            <div className={`max-w-md mx-auto ${t.card} min-h-screen shadow-2xl relative`}>
                <div className="relative h-96 w-full">
                    <img src={eventDetails.generatedImage} alt="Event Theme" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                    <div className="absolute bottom-0 left-0 p-6 text-white w-full">
                        <p className="text-sm font-medium uppercase tracking-wider mb-1 opacity-90">
                            Hosted by {eventDetails.pageSettings?.coHosts || hostName}
                        </p>
                        <h1 className="text-4xl font-extrabold leading-tight mb-2 drop-shadow-md">{eventDetails.title}</h1>
                    </div>
                </div>

                <div className={`px-6 py-8 relative -mt-6 ${t.card} rounded-t-3xl`}>
                    
                    {renderSpotify()}

                    <div className="flex items-start mb-8">
                        <div className={`w-12 h-12 rounded-xl ${t.inputBg} flex flex-col items-center justify-center flex-shrink-0 mr-4 border ${t.border}`}>
                            <span className={`text-xs font-bold ${t.muted} uppercase`}>{new Date(eventDetails.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                            <span className={`text-lg font-extrabold ${t.text}`}>{new Date(eventDetails.date).getDate()}</span>
                        </div>
                        <div>
                            <p className={`text-lg font-bold ${t.text}`}>{new Date(`${eventDetails.date}T${eventDetails.time}`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                            <p className={t.muted}>{eventDetails.time}</p>
                        </div>
                    </div>

                    {timeLeft && (
                        <div className="flex justify-between items-center mb-8 gap-2">
                            <div className={`flex flex-col items-center justify-center p-3 rounded-xl flex-1 border ${t.border} ${t.inputBg}`}>
                                <span className={`text-xl font-bold ${t.text}`}>{timeLeft.days}</span>
                                <span className={`text-[10px] uppercase font-bold tracking-widest ${t.muted}`}>Days</span>
                            </div>
                            <div className={`flex flex-col items-center justify-center p-3 rounded-xl flex-1 border ${t.border} ${t.inputBg}`}>
                                <span className={`text-xl font-bold ${t.text}`}>{timeLeft.hours}</span>
                                <span className={`text-[10px] uppercase font-bold tracking-widest ${t.muted}`}>Hours</span>
                            </div>
                            <div className={`flex flex-col items-center justify-center p-3 rounded-xl flex-1 border ${t.border} ${t.inputBg}`}>
                                <span className={`text-xl font-bold ${t.text}`}>{timeLeft.minutes}</span>
                                <span className={`text-[10px] uppercase font-bold tracking-widest ${t.muted}`}>Mins</span>
                            </div>
                        </div>
                    )}

                    <div className="flex items-start mb-8">
                        <div className={`w-12 h-12 rounded-xl ${t.inputBg} flex items-center justify-center flex-shrink-0 mr-4 border ${t.border}`}>
                            <span className={`text-xl ${t.text}`}>📍</span>
                        </div>
                        <div className="flex-1">
                            <p className={`text-lg font-bold ${t.text}`}>Location</p>
                            <p className={t.muted}>{eventDetails.location}</p>
                            {eventDetails.locationDetails && <p className={`text-sm mt-0.5 font-medium ${t.muted}`}>{eventDetails.locationDetails}</p>}
                            <a href={`https://maps.google.com/?q=${encodeURIComponent(eventDetails.location)}`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 font-semibold hover:underline" style={textStyle}>
                                Get Directions
                            </a>
                        </div>
                    </div>

                    <div className="mb-8">
                        <p className={`text-lg leading-relaxed whitespace-pre-wrap ${t.muted}`}>
                            {eventDetails.generatedText || "We can't wait to celebrate with you!"}
                        </p>
                    </div>

                    {renderRegistry()}
                    {renderTimeline()}

                    {eventDetails?.enablePhotoDrop && (
                        <div className={`border-t ${t.border} pt-8 mt-8`}>
                            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                                <h3 className={`text-xl font-extrabold ${t.text}`}>Photo Drop</h3>
                                <div className={`flex ${t.inputBg} rounded-lg p-1 w-full sm:w-auto shadow-inner border ${t.border}`}>
                                    <button 
                                        type="button"
                                        onClick={() => setPhotoTab('gallery')} 
                                        className={`flex-1 sm:flex-none px-6 py-2 text-sm font-bold rounded-md transition-all ${photoTab === 'gallery' ? `${t.card} shadow ${t.text}` : `${t.muted} hover:opacity-80`}`}
                                    >
                                        Gallery
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setPhotoTab('upload')} 
                                        className={`flex-1 sm:flex-none px-6 py-2 text-sm font-bold rounded-md transition-all ${photoTab === 'upload' ? `${t.card} shadow ${t.text}` : `${t.muted} hover:opacity-80`}`}
                                    >
                                        Upload
                                    </button>
                                </div>
                            </div>

                            {photoTab === 'upload' ? (
                                <div className="animate-fade-in space-y-4">
                                    <div className={`border-2 border-dashed ${t.border} rounded-xl p-10 text-center hover:opacity-80 transition cursor-pointer`}>
                                        <input 
                                            type="file" 
                                            multiple 
                                            accept="image/*" 
                                            onChange={handlePhotoUpload} 
                                            className="hidden" 
                                            id="photo-upload-input"
                                            disabled={isUploadingPhotos}
                                        />
                                        <label htmlFor="photo-upload-input" className={`cursor-pointer flex flex-col items-center w-full h-full ${isUploadingPhotos ? 'opacity-50 pointer-events-none' : ''}`}>
                                            <span className="text-5xl mb-4">{isUploadingPhotos ? '⏳' : '📸'}</span>
                                            <span className={`text-sm font-bold ${t.text} ${t.inputBg} px-6 py-2.5 rounded-full hover:opacity-80 transition border ${t.border}`}>
                                                {isUploadingPhotos ? 'Uploading...' : 'Tap to Select Photos'}
                                            </span>
                                            <p className={`text-xs ${t.muted} mt-3 font-medium`}>Max 5MB per photo</p>
                                        </label>
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => setPhotoTab('gallery')}
                                        className={`w-full py-3 text-sm font-bold ${t.muted} hover:opacity-80 transition`}
                                    >
                                        ← Back to Gallery
                                    </button>
                                </div>
                            ) : (
                                <div className="animate-fade-in">
                                    {photos && photos.length > 0 ? (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {photos.map((photo) => (
                                                <div key={photo.id} className={`aspect-square rounded-lg overflow-hidden relative group ${t.inputBg}`}>
                                                    <img src={photo.url} alt="Event moment" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={`text-center py-12 ${t.inputBg} rounded-xl border ${t.border}`}>
                                            <span className="text-3xl mb-2 block">✨</span>
                                            <p className={`text-sm font-medium ${t.muted}`}>No photos yet. Be the first to share!</p>
                                            <button 
                                                onClick={() => setPhotoTab('upload')} 
                                                className="mt-4 font-bold text-sm hover:underline"
                                                style={textStyle}
                                            >
                                                Upload a photo
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {renderGuestbook()}
                    {renderPotluck()}

                    <div id="rsvp-form-section" className={`border-t ${t.border} pt-8`}>
                        <h3 className={`text-xl font-extrabold text-center mb-6 ${t.text}`}>Are you in?</h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                <label className={`cursor-pointer text-center p-4 rounded-xl border-2 transition-all ${isAttending ? 'bg-opacity-10 border-opacity-100' : `${t.border} hover:opacity-80`}`} style={isAttending ? { borderColor: themeColor, backgroundColor: `${themeColor}10` } : {}}>
                                    <input type="radio" name="status" className="hidden" checked={isAttending} onChange={() => setIsAttending(true)} />
                                    <span className="block text-2xl mb-1">👍</span>
                                    <span className={`font-bold block`} style={isAttending ? textStyle : { color: 'inherit' }}>Going</span>
                                </label>
                                <label className={`cursor-pointer text-center p-4 rounded-xl border-2 transition-all ${!isAttending ? `${t.inputBg} ${t.border}` : 'border-transparent hover:opacity-80'}`}>
                                    <input type="radio" name="status" className="hidden" checked={!isAttending} onChange={() => setIsAttending(false)} />
                                    <span className="block text-2xl mb-1">👎</span>
                                    <span className={`font-bold block ${t.muted}`}>Can't Go</span>
                                </label>
                            </div>

                            {isAttending && (
                                <div className="space-y-4 animate-fade-in">
                                    <div>
                                        <label className={`block text-sm font-bold ${t.muted} mb-1`}>Your Name</label>
                                        <input 
                                            type="text" 
                                            required 
                                            value={name} 
                                            onChange={(e) => setName(e.target.value)} 
                                            className={`w-full p-3 ${t.card} border ${t.border} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition ${t.text}`}
                                            style={{ '--tw-ring-color': themeColor }}
                                            placeholder="Jane Doe"
                                        />
                                    </div>
                                    
                                    {eventDetails.pageSettings?.customQuestions?.map((q, idx) => (
                                        <div key={idx}>
                                            <label className={`block text-sm font-bold ${t.muted} mb-1`}>{q}</label>
                                            <input 
                                                type="text" 
                                                value={customAnswers[q] || ''} 
                                                onChange={(e) => setCustomAnswers({...customAnswers, [q]: e.target.value})} 
                                                className={`w-full p-3 ${t.card} border ${t.border} rounded-xl focus:outline-none focus:ring-2 transition ${t.text}`}
                                                style={{ '--tw-ring-color': themeColor }}
                                                placeholder="Your answer" 
                                            />
                                        </div>
                                    ))}

                                    {eventDetails.polls?.length > 0 && (
                                        <div className="space-y-4">
                                            {eventDetails.polls.map(poll => (
                                                <div key={poll.id} className={`${t.inputBg} p-4 rounded-xl border ${t.border}`}>
                                                    <label className={`block text-sm font-bold ${t.text} mb-2`}>{poll.question}</label>
                                                    <div className="space-y-2">
                                                        {poll.options.map(option => (
                                                            <label key={option} className={`flex items-center space-x-3 cursor-pointer ${t.card} p-2 rounded-lg border ${t.border} hover:opacity-80 transition`}>
                                                                <input
                                                                    type="radio"
                                                                    name={`poll_${poll.id}`}
                                                                    value={option}
                                                                    checked={pollAnswers[poll.id] === option}
                                                                    onChange={() => setPollAnswers(prev => ({ ...prev, [poll.id]: option }))}
                                                                    className="h-4 w-4 focus:ring-pink-500 border-gray-300"
                                                                    style={{ color: themeColor }}
                                                                />
                                                                <span className={`${t.text} text-sm`}>{option}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {eventDetails.pageSettings?.allowSongRequests && (
                                        <div className="animate-fade-in">
                                            <div className="flex items-center justify-between mb-2 px-1">
                                                <label className={`text-lg font-bold ${t.text}`}>Request Music</label>
                                                <span className={`text-[10px] font-bold ${t.inputBg} ${t.muted} border ${t.border} px-2 py-1 rounded-md uppercase tracking-wide`}>Powered by Apple Music</span>
                                            </div>
                                            
                                            {!selectedSong ? (
                                                <div className="space-y-4">
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <svg className={`h-5 w-5 ${t.muted}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                                        </div>
                                                        <input 
                                                            type="text" 
                                                            value={songSearchQuery} 
                                                            onChange={(e) => setSongSearchQuery(e.target.value)} 
                                                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchTracks())}
                                                            className={`w-full pl-10 pr-4 py-3 ${t.inputBg} border ${t.border} rounded-xl focus:ring-2 focus:bg-white transition-all text-base font-medium ${t.text}`}
                                                            style={{ '--tw-ring-color': themeColor }}
                                                            placeholder="Artists, Songs, Lyrics, and more" 
                                                        />
                                                        {isSearchingSongs && (
                                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                                <div className={`animate-spin h-4 w-4 border-2 ${t.muted} border-t-transparent rounded-full`}></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    {songSearchResults.length > 0 && (
                                                        <div className={`${t.card} rounded-xl shadow-xl border ${t.border} overflow-hidden divide-y ${t.border.replace('border-', 'divide-')}`}>
                                                            {songSearchResults.map((track) => (
                                                                <div 
                                                                    key={track.trackId}
                                                                    onClick={() => { setSelectedSong(track); setSongSearchResults([]); setPlayingPreview(null); if(audioRef.current) audioRef.current.pause(); }}
                                                                    className="flex items-center gap-3 p-3 hover:opacity-80 cursor-pointer group transition-colors"
                                                                >
                                                                    <div className="relative w-12 h-12 flex-shrink-0 group-hover:shadow-md transition-shadow rounded-md overflow-hidden">
                                                                        <img src={track.artworkUrl60.replace('60x60', '120x120')} alt={track.trackName} className="w-full h-full object-cover" />
                                                                        {track.previewUrl && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => { e.stopPropagation(); togglePreview(track.previewUrl); }}
                                                                                className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                            >
                                                                                <span className="text-white text-xs">{playingPreview === track.previewUrl ? '⏸' : '▶'}</span>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-grow min-w-0">
                                                                        <p className={`text-sm font-semibold ${t.text} truncate`}>{track.trackName}</p>
                                                                        <p className={`text-xs ${t.muted} truncate`}>{track.artistName}</p>
                                                                    </div>
                                                                    <button className="w-7 h-7 rounded-full border flex items-center justify-center transition" style={{ borderColor: themeColor, color: themeColor }}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className={`flex items-center gap-4 p-4 ${t.card} border ${t.border} rounded-xl shadow-sm relative overflow-hidden`}>
                                                    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: themeColor }}></div>
                                                    <div className="relative w-14 h-14 flex-shrink-0 shadow-md rounded-md overflow-hidden">
                                                        <img src={selectedSong.artworkUrl60.replace('60x60', '120x120')} alt={selectedSong.trackName} className="w-full h-full object-cover" />
                                                        {selectedSong.previewUrl && (
                                                            <button
                                                                type="button"
                                                                onClick={() => togglePreview(selectedSong.previewUrl)}
                                                                className="absolute inset-0 bg-black/20 flex items-center justify-center"
                                                            >
                                                                <span className="text-white font-bold">{playingPreview === selectedSong.previewUrl ? '⏸' : '▶'}</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow min-w-0">
                                                        <p className={`text-sm font-bold ${t.text} truncate`}>{selectedSong.trackName}</p>
                                                        <p className={`text-xs ${t.muted} truncate`}>{selectedSong.artistName}</p>
                                                        <p className="text-[10px] font-bold mt-1 uppercase tracking-wide" style={{ color: themeColor }}>Selected</p>
                                                    </div>
                                                    <button 
                                                        type="button"
                                                        onClick={() => { setSelectedSong(null); if(audioRef.current) audioRef.current.pause(); setPlayingPreview(null); }}
                                                        className={`${t.muted} hover:text-red-500 p-2`}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>
                                                </div>
                                            )}

                                            {playlist.length > 0 && (
                                                <div className="mt-8">
                                                    <div className="flex items-center justify-between mb-3 px-1">
                                                        <h4 className={`font-bold text-xl ${t.text}`}>Up Next</h4>
                                                        <span className={`text-xs font-semibold ${t.muted}`}>{playlist.length} songs</span>
                                                    </div>
                                                    <div className={`${t.inputBg} rounded-2xl p-1 space-y-1 border ${t.border}`}>
                                                        {playlist.map((item, idx) => (
                                                            <div key={idx} className={`flex items-center gap-3 p-2 rounded-xl hover:opacity-80 transition group`}>
                                                                {item.songRequestData ? (
                                                                    <>
                                                                        <div className="relative w-10 h-10 flex-shrink-0 rounded-md overflow-hidden shadow-sm group-hover:shadow-md transition-all">
                                                                            <img src={item.songRequestData.artworkUrl60} alt="Art" className="w-full h-full object-cover" />
                                                                            {item.songRequestData.previewUrl && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => { e.preventDefault(); togglePreview(item.songRequestData.previewUrl); }}
                                                                                    className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-all"
                                                                                >
                                                                                    <span className="text-white text-xs opacity-0 group-hover:opacity-100">{playingPreview === item.songRequestData.previewUrl ? '⏸' : '▶'}</span>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        <div className="min-w-0 flex-grow">
                                                                            <p className={`text-sm font-semibold ${t.text} truncate leading-tight`}>{item.songRequestData.trackName}</p>
                                                                            <p className={`text-xs ${t.muted} truncate`}>{item.songRequestData.artistName}</p>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <div className="flex items-center gap-3 w-full">
                                                                        <div className={`w-10 h-10 rounded-md ${t.card} border ${t.border} flex items-center justify-center text-lg flex-shrink-0 ${t.muted}`}>🎵</div>
                                                                        <div className="min-w-0 flex-grow">
                                                                            <p className={`text-sm font-semibold ${t.text} truncate`}>{item.songRequest}</p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div className="flex-shrink-0">
                                                                    <img 
                                                                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(item.name || 'G')}&background=random&color=fff&size=32`} 
                                                                        alt={item.name}
                                                                        className="w-6 h-6 rounded-full border border-white shadow-sm"
                                                                        title={`Requested by ${item.name}`}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {eventDetails.allowPlusOnes && (
                                        <div className="mt-4">
                                            <label className={`block text-sm font-bold ${t.muted} mb-1`}>Bringing a guest? (Optional)</label>
                                            <input 
                                                type="text" 
                                                value={plusOneName} 
                                                onChange={(e) => setPlusOneName(e.target.value)} 
                                                className={`w-full p-3 ${t.card} border ${t.border} rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition ${t.text}`}
                                                style={{ '--tw-ring-color': themeColor }}
                                                placeholder="+1's Name" 
                                            />
                                        </div>
                                    )}

                                    {eventDetails.mealOptions && eventDetails.mealOptions.length > 0 && (
                                        <div className="grid grid-cols-1 gap-3">
                                            <select value={mealChoice} onChange={(e) => setMealChoice(e.target.value)} className={`w-full p-3 ${t.card} border ${t.border} rounded-xl ${t.text}`}>
                                                <option value="">Select Meal Preference</option>
                                                {eventDetails.mealOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                            </select>
                                            {plusOneName.trim() && (
                                                <select value={plusOneMealChoice} onChange={(e) => setPlusOneMealChoice(e.target.value)} className={`w-full p-3 ${t.card} border ${t.border} rounded-xl ${t.text}`}>
                                                    <option value="">Select +1's Meal Preference</option>
                                                    {eventDetails.mealOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <button 
                                type="submit" 
                                className="w-full py-4 rounded-xl font-bold text-white text-lg shadow-lg hover:opacity-90 transform hover:-translate-y-0.5 transition-all mt-6"
                                style={bgStyle}
                            >
                                {isAttending ? "Count Me In!" : "Send RSVP"}
                            </button>
                        </form>
                    </div>
                </div>
                
                <div className="h-28 md:hidden"></div>

                {!submitted && (
                    <div className="fixed bottom-0 left-0 w-full p-4 bg-white/90 backdrop-blur-md border-t border-gray-200 z-[90] md:hidden animate-fade-in-up flex justify-center shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
                        <button 
                            onClick={() => document.getElementById('rsvp-form-section')?.scrollIntoView({ behavior: 'smooth' })}
                            className="w-full max-w-sm py-4 rounded-xl font-bold text-white shadow-lg text-lg transition-transform active:scale-95"
                            style={bgStyle}
                        >
                            RSVP Now
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}